import React, { useState, useRef, useEffect } from 'react';

const DEFAULT_OPTIONS = [];
const DEFAULT_SEARCH_FIELDS = ["name", "description" , "productId"];

const AutocompleteSelect = ({
    value,
    onChange,
    options = DEFAULT_OPTIONS,
    placeholder = "Search...",
    displayField = "name",
    searchFields = DEFAULT_SEARCH_FIELDS, // Array of fields to search in
    valueField = "_id",
    required = false,
    className = "form-control",
    renderOption = null,
    optionDisabled = null
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const wrapperRef = useRef(null);

    useEffect(() => {
        // Filter options based on search term
        if (searchTerm) {
            const filtered = options.filter(option => {
                if (typeof option === 'string') {
                    return option.toLowerCase().includes(searchTerm.toLowerCase());
                }

                // Search in multiple fields
                const fieldsToSearch = searchFields.length > 0 ? searchFields : [displayField];
                return fieldsToSearch.some(field => {
                    const fieldValue = option[field];
                    return fieldValue?.toString().toLowerCase().includes(searchTerm.toLowerCase());
                });
            });
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(options);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, options, displayField, searchFields]);

    useEffect(() => {
        // Set initial display value if value is provided
        if (value && options.length > 0) {
            const selected = options.find(opt => {
                const optValue = typeof opt === 'string' ? opt : opt[valueField];
                return optValue === value;
            });
            if (selected) {
                const displayValue = typeof selected === 'string' ? selected : selected[displayField];
                setSearchTerm(displayValue || '');
            }
        } else if (!value && !searchTerm) {
            setSearchTerm('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options, displayField, valueField]); // Removed searchTerm from dependencies

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setSearchTerm(newValue);
        setShowDropdown(true);

        // Clear selection if input is cleared
        if (!newValue) {
            onChange('');
        }
    };

    const handleOptionClick = (option) => {
        const optValue = typeof option === 'string' ? option : option[valueField];
        const displayValue = typeof option === 'string' ? option : option[displayField];

        setSearchTerm(displayValue || '');
        setShowDropdown(false);
        onChange(optValue);
    };

    const handleInputFocus = () => {
        setShowDropdown(true);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                type="text"
                className={className}
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                required={required}
                autoComplete="off"
            />

            {showDropdown && filteredOptions.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '280px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                    zIndex: 999999,
                    marginTop: '4px'
                }}>
                    {filteredOptions.map((option, index) => {
                        const optValue = typeof option === 'string' ? option : option[valueField];
                        const isSelected = optValue === value;
                        const isDisabled = optionDisabled ? optionDisabled(option) : false;

                        return (
                            <div
                                key={index}
                                onClick={() => {
                                    if (!isDisabled) handleOptionClick(option);
                                }}
                                style={{
                                    padding: '12px 16px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: isDisabled ? '#f9fafb' : (isSelected ? '#f0f9ff' : '#ffffff'),
                                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'all 0.15s ease',
                                    fontSize: '14px',
                                    color: isDisabled ? '#9ca3af' : (isSelected ? '#0369a1' : '#374151'),
                                    fontWeight: isSelected ? '500' : '400',
                                    opacity: isDisabled ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!isDisabled) {
                                        e.currentTarget.style.backgroundColor = isSelected ? '#e0f2fe' : '#f9fafb';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isDisabled) {
                                        e.currentTarget.style.backgroundColor = isSelected ? '#f0f9ff' : '#ffffff';
                                    }
                                }}
                            >
                                {renderOption ? renderOption(option) : (
                                    <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                        {typeof option === 'string' ? option : option[displayField]}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showDropdown && searchTerm && filteredOptions.length === 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '14px 16px',
                    marginTop: '4px',
                    color: '#9ca3af',
                    fontSize: '14px',
                    textAlign: 'center',
                    zIndex: 999999
                }}>
                    No results found
                </div>
            )}
        </div>
    );
};

export default AutocompleteSelect;
